import React, { useState } from 'react';
import { X, DollarSign, Calendar } from 'lucide-react';

const DaycareBillingModal = ({ isOpen, onClose, onSuccess, academicYear, apiCall, showError, showSuccess }) => {
  const [processDate, setProcessDate] = useState(new Date().toISOString().split('T')[0]);
  const [processing, setProcessing] = useState(false);

  const handleProcessCharges = async (e) => {
    e.preventDefault();

    if (!window.confirm(`Process monthly charges for all active enrollments?\n\nProcess Date: ${processDate}\nAcademic Year: ${academicYear}`)) {
      return;
    }

    setProcessing(true);
    try {
      const result = await apiCall('/daycare/process-charges', {
        method: 'POST',
        body: JSON.stringify({
          processDate: processDate,
          academicYear: academicYear
        })
      });

      if (result.success) {
        const summary = result.summary || {};
        showSuccess(
          `✅ Monthly charges processed successfully!\n\n` +
          `Charges Generated: ${summary.ChargesGenerated || 0}\n` +
          `Total Amount: $${summary.TotalAmount?.toFixed(2) || '0.00'}\n` +
          `Families Affected: ${summary.FamiliesAffected || 0}`
        );
        onSuccess();
      } else {
        showError('Failed to process charges: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      showError('Error processing charges: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-medium text-gray-900">Process Monthly Charges</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleProcessCharges}>
            <div className="px-6 py-4">
              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-900 mb-2">What This Does</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Generates monthly charges for all active daycare enrollments</li>
                  <li>• Creates entries in MonthlyCharges table</li>
                  <li>• Charges can then be applied to family balances in Payment module</li>
                </ul>
              </div>

              {/* Process Date */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Process Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="date"
                    value={processDate}
                    onChange={(e) => setProcessDate(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Date when charges should be recorded
                </p>
              </div>

              {/* Academic Year */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Academic Year
                </label>
                <input
                  type="text"
                  value={academicYear}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Only active enrollments for this year will be processed
                </p>
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> This action will generate charges for all active enrollments. 
                  Make sure you haven't already processed charges for this month.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-300"
              >
                <DollarSign className="w-4 h-4" />
                {processing ? 'Processing...' : 'Process Charges'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DaycareBillingModal;